import { useRef, useState } from "react";
import useNetwork from "../hooks/networkHook";
import { useTranslation } from "react-i18next";
import { Alert, Form } from "react-bootstrap";
import PropTypes from 'prop-types';
import Icon from "./Icon";
import { Validator } from "./ValidationResult";
import TextInput from "./TextInput";

export const customCtrlDef = {
    toString: ({ value, defaultValue }) => value ? '<attachment>' : defaultValue,
    control: ({ def, value, onChange, ...props }) => {
        const srcURL = value?.srcURL ?? def.srcURL;
        const destURL = value?.destURL ?? def.destURL;
        const mime = value?.mime ?? def.mime;
        const handleChange = destURL || srcURL ? undefined : onChange;
        return <Attachment filename={value?.name} mime={mime} srcURL={srcURL} destURL={destURL} onSelect={handleChange} {...props}></Attachment>
    }
}

/**
 * A file handling component which uploads and downloads files from a specified URL
 * @component
 */
const Attachment = ({ style, filename, mime, srcURL, destURL, onSelect, disabled = false, required = false }) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const fileInputRef = useRef();
    const validityRef = useRef();
    const [error, setError] = useState();
    const [isDragOver,setIsDragOver] = useState(false);
    const [validity, setValidity] = useState(null);

    const processFile = file => {  
        if (onSelect) {
            onSelect(file);
        }
        if (destURL) {
            const formData = new FormData();
            formData.append('file', file);
            try {
                network.postData(destURL, formData);
            } catch (err) {
                setError(err);
            }
        }
    }

    const handleDrop = event => {
        if (srcURL) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
        if (event.dataTransfer.items?.length === 1 && event.dataTransfer.items[0].kind === 'file') {
            processFile(event.dataTransfer.files[0]);
        }
    }

    const handleFileChange = event => {
        const file = event.target.files[0];
        processFile(file);
    }

    const handleDragOver = event => {
        if (srcURL) {
            return;
        }
        if (error) {
            setError();
        }
        if (event.dataTransfer.items?.length === 1 && event.dataTransfer.items[0].kind === 'file') {
            event.preventDefault();
            event.stopPropagation();
            setIsDragOver(true);
        }
    }

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleClick = async () => {
        if (destURL || onSelect) {
            fileInputRef.current?.click();
        } else {
            try {
                let downloadName = filename;
                const response = await network.fetchData(srcURL);
                if (!downloadName) {
                    let disposition = response.headers.get('Content-Disposition');
                    if (disposition && disposition.indexOf('attachment') !== -1) {
                        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                        const matches = filenameRegex.exec(disposition);
                        if (matches?.[1]) {
                            downloadName = matches[1].replace(/['"]/g, '');
                        }
                    }
                }
                if (downloadName && !downloadName.match(/\.\w{3}$/)) {
                    switch (mime) {
                        case 'image/png':
                            downloadName += '.png';
                            break;
                        case 'image/jpg':
                        case 'image/jpeg':
                            downloadName += '.jpg';
                            break;
                        case 'application/pdf':
                            downloadName += '.pdf';
                            break;
                        default:
                            break;
                    }
                }
                const blob = await response.blob();
                network.downloadBlob(blob, downloadName);
            } catch (err) {
                setError(err);
            }
        }
    }

    let baseStyle = { };
    if (isDragOver) {
        baseStyle.border = '3px solid blue';
        baseStyle.textAlign = 'center';
    } else if (!srcURL) {
        baseStyle.border = '3px dashed lightgrey';
        baseStyle.cursor = 'pointer';
        baseStyle.textAlign = 'center';
    } else {
        baseStyle.cursor = 'pointer';
    }

    if (disabled) {
        if (srcURL) {
            return <div style={{ ...baseStyle, ...style }} onClick={handleClick}><Icon intent="download"></Icon> {t('downloadFile')}</div>
        }
        return null;
    }

    const isInvalid = (validity === false) || error;

    return <div style={{ ...baseStyle, ...style }} onClick={handleClick} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
        {error ? <Alert variant="danger">{error.message}</Alert> : null}
        <Icon intent={srcURL ? 'download' : 'upload'}></Icon> {t(srcURL ? 'downloadFile' : 'fileUploadPrompt')} {filename}
        <Validator ref={validityRef} onValidation={setValidity} nonblockingError={error}>
            <TextInput ref={validityRef} value={filename} required={!srcURL && (destURL || onSelect) && required} style={{display: 'none'}}></TextInput>
            <Form.File
                id="custom-file"
                isInvalid={isInvalid}
                label={t('fileUploadPrompt')}
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                /> 
        </Validator>
    </div>
}

Attachment.propTypes = {
    /** URL to download the file */
    srcURL: PropTypes.string,
    /** URL to upload the file */
    destURL: PropTypes.string,
    /** Functon to call when a new file is selected */
    onSelect: PropTypes.func,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    style: PropTypes.object
}

export default Attachment;